import type {
	AcceptMatchRequest,
	ApproveDecisionRequest,
	ApproveEntryRequest,
	BatchPostEntriesRequest,
	BatchReverseEntriesRequest,
	ClosePeriodRequest,
	CreateAccountRequest,
	CreateFixedAssetRequest,
	CreateRecurringEntryRequest,
	CreateReconSessionRequest,
	DeleteRecurringEntryRequest,
	DisposeFixedAssetRequest,
	DraftEntryRequest,
	FinalizeReconRequest,
	GetAccountStatementRequest,
	GetCounterpartyReconRequest,
	GetReportRequest,
	ListAccountsRequest,
	ListDecisionsRequest,
	ListEntriesRequest,
	ListFixedAssetsRequest,
	ListIcTransactionsRequest,
	ListPeriodsRequest,
	PostEntryRequest,
	RejectDecisionRequest,
	ReopenPeriodRequest,
	ReverseEntryRequest,
	RunAutoMatchRequest,
	RunCloseAgentRequest,
	RunConsolidationRequest,
	RunQualityCheckRequest,
	SetAutonomyConfigRequest,
	SetBudgetAmountRequest,
	StoreSchemaRequest,
	UpdateAccountRequest,
	UpdateRecurringEntryRequest,
} from "@/gen/omnios/v1/accounting_pb";
import { PeriodStatus } from "@/gen/omnios/v1/accounting_pb";
import { accountingClient as client } from "@/lib/accounting-client";
import type { PartialMessage } from "@bufbuild/protobuf";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

const DEFAULT_LIST_PAGE_SIZE = 500;

// ---------------------------------------------------------------------------
// Journal Entries
// ---------------------------------------------------------------------------

export function useListEntries(filters?: PartialMessage<ListEntriesRequest>) {
	return useQuery({
		queryKey: ["accounting", "entries", filters],
		queryFn: () =>
			client.listEntries({
				page: filters?.page ?? {
					pageSize: DEFAULT_LIST_PAGE_SIZE,
					pageToken: "",
				},
				...filters,
			}),
	});
}

export function useGetEntry(id: string) {
	return useQuery({
		queryKey: ["accounting", "entries", id],
		queryFn: () => client.getEntry({ id }),
		enabled: !!id,
	});
}

export function usePostEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<PostEntryRequest>) =>
			client.postEntry(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
		},
	});
}

export function useDraftEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<DraftEntryRequest>) =>
			client.draftEntry(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
		},
	});
}

export function useApproveEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<ApproveEntryRequest>) =>
			client.approveEntry(req),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
			if (vars.id) {
				qc.invalidateQueries({
					queryKey: ["accounting", "entries", vars.id],
				});
			}
		},
	});
}

export function useReverseEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<ReverseEntryRequest>) =>
			client.reverseEntry(req),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
			if (vars.id) {
				qc.invalidateQueries({
					queryKey: ["accounting", "entries", vars.id],
				});
			}
		},
	});
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

export function useListAccounts(filters?: PartialMessage<ListAccountsRequest>) {
	return useQuery({
		queryKey: ["accounting", "accounts", filters],
		queryFn: () =>
			client.listAccounts({
				page: filters?.page ?? {
					pageSize: DEFAULT_LIST_PAGE_SIZE,
					pageToken: "",
				},
				...filters,
			}),
	});
}

export function useGetAccount(id: string) {
	return useQuery({
		queryKey: ["accounting", "accounts", id],
		queryFn: () => client.getAccount({ id }),
		enabled: !!id,
	});
}

export function useCreateAccount() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<CreateAccountRequest>) =>
			client.createAccount(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "accounts"] });
		},
	});
}

export function useUpdateAccount() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<UpdateAccountRequest>) =>
			client.updateAccount(req),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: ["accounting", "accounts"] });
			if (vars.id) {
				qc.invalidateQueries({
					queryKey: ["accounting", "accounts", vars.id],
				});
			}
		},
	});
}

// ---------------------------------------------------------------------------
// Balances & Reports
// ---------------------------------------------------------------------------

export function useGetBalance(
	accountCode: string,
	periodId?: string,
	basis?: string,
) {
	return useQuery({
		queryKey: ["accounting", "balance", accountCode, periodId, basis],
		queryFn: () => client.getBalance({ accountCode, periodId, basis }),
		enabled: !!accountCode,
	});
}

export function useGetTrialBalance(periodId: string, basis?: string) {
	return useQuery({
		queryKey: ["accounting", "trial-balance", periodId, basis],
		queryFn: () => client.getTrialBalance({ periodId, basis }),
		enabled: !!periodId,
	});
}

export function useGetIncomeStatement(periodId: string, basis?: string) {
	return useQuery({
		queryKey: ["accounting", "income-statement", periodId, basis],
		queryFn: () =>
			client.getIncomeStatement({
				periodId,
				basis,
			} as PartialMessage<GetReportRequest>),
		enabled: !!periodId,
	});
}

export function useGetBalanceSheet(periodId: string, basis?: string) {
	return useQuery({
		queryKey: ["accounting", "balance-sheet", periodId, basis],
		queryFn: () =>
			client.getBalanceSheet({
				periodId,
				basis,
			} as PartialMessage<GetReportRequest>),
		enabled: !!periodId,
	});
}

export function useGetCashFlowStatement(periodId: string, basis?: string) {
	return useQuery({
		queryKey: ["accounting", "cash-flow", periodId, basis],
		queryFn: () =>
			client.getCashFlowStatement({
				periodId,
				basis,
			} as PartialMessage<GetReportRequest>),
		enabled: !!periodId,
	});
}

export function useGetAccountStatement(
	accountCode: string,
	params?: Omit<PartialMessage<GetAccountStatementRequest>, "accountCode">,
) {
	return useQuery({
		queryKey: ["accounting", "account-statement", accountCode, params],
		queryFn: () => client.getAccountStatement({ accountCode, ...params }),
		enabled: !!accountCode,
	});
}

// ---------------------------------------------------------------------------
// Period Close
// ---------------------------------------------------------------------------

export function useListPeriods(filters?: PartialMessage<ListPeriodsRequest>) {
	return useQuery({
		queryKey: ["accounting", "periods", filters],
		queryFn: () =>
			client.listPeriods({
				page: filters?.page ?? {
					pageSize: DEFAULT_LIST_PAGE_SIZE,
					pageToken: "",
				},
				...filters,
			}),
	});
}

export function useGetCloseChecklist(periodId: string) {
	return useQuery({
		queryKey: ["accounting", "close-checklist", periodId],
		queryFn: () => client.getCloseChecklist({ periodId }),
		enabled: !!periodId,
	});
}

export function useRunCloseAgent() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<RunCloseAgentRequest>) =>
			client.runCloseAgent(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
			qc.invalidateQueries({ queryKey: ["accounting", "periods"] });
		},
	});
}

export function useClosePeriod() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<ClosePeriodRequest>) =>
			client.closePeriod(req),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: ["accounting", "periods"] });
			if (vars.periodId) {
				qc.invalidateQueries({
					queryKey: ["accounting", "close-checklist", vars.periodId],
				});
			}
		},
	});
}

export function useReopenPeriod() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<ReopenPeriodRequest>) =>
			client.reopenPeriod(req),
		onSuccess: (_data, vars) => {
			qc.invalidateQueries({ queryKey: ["accounting", "periods"] });
			if (vars.periodId) {
				qc.invalidateQueries({
					queryKey: ["accounting", "close-checklist", vars.periodId],
				});
			}
		},
	});
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

export function useListSchemas() {
	return useQuery({
		queryKey: ["accounting", "schemas"],
		queryFn: () => client.listSchemas({}),
	});
}

export function useGetSchema(key: string) {
	return useQuery({
		queryKey: ["accounting", "schemas", key],
		queryFn: () => client.getSchema({ key }),
		enabled: !!key,
	});
}

export function useStoreSchema() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<StoreSchemaRequest>) =>
			client.storeSchema(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "schemas"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export function useCreateReconSession() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<CreateReconSessionRequest>) =>
			client.createReconSession(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "recon"] });
		},
	});
}

export function useGetReconSession(id: string) {
	return useQuery({
		queryKey: ["accounting", "recon", id],
		queryFn: () => client.getReconSession({ id }),
		enabled: !!id,
	});
}

export function useRunAutoMatch() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<RunAutoMatchRequest>) =>
			client.runAutoMatch(req),
		onSuccess: (_data, vars) => {
			if (vars.sessionId) {
				qc.invalidateQueries({
					queryKey: ["accounting", "recon", vars.sessionId],
				});
			}
		},
	});
}

export function useAcceptMatch() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<AcceptMatchRequest>) =>
			client.acceptMatch(req),
		onSuccess: (_data, vars) => {
			if (vars.sessionId) {
				qc.invalidateQueries({
					queryKey: ["accounting", "recon", vars.sessionId],
				});
			}
		},
	});
}

export function useFinalizeRecon() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<FinalizeReconRequest>) =>
			client.finalizeRecon(req),
		onSuccess: (_data, vars) => {
			if (vars.sessionId) {
				qc.invalidateQueries({
					queryKey: ["accounting", "recon", vars.sessionId],
				});
			}
		},
	});
}

// ---------------------------------------------------------------------------
// Agents
// ---------------------------------------------------------------------------

export function useListDecisions(
	filters?: PartialMessage<ListDecisionsRequest>,
) {
	return useQuery({
		queryKey: ["accounting", "decisions", filters],
		queryFn: () => client.listDecisions(filters ?? {}),
	});
}

export function useApproveDecision() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<ApproveDecisionRequest>) =>
			client.approveDecision(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "decisions"] });
		},
	});
}

export function useRejectDecision() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<RejectDecisionRequest>) =>
			client.rejectDecision(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "decisions"] });
		},
	});
}

export function useGetAutonomyConfig() {
	return useQuery({
		queryKey: ["accounting", "autonomy-config"],
		queryFn: () => client.getAutonomyConfig({}),
	});
}

export function useSetAutonomyConfig() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<SetAutonomyConfigRequest>) =>
			client.setAutonomyConfig(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "autonomy-config"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Budget
// ---------------------------------------------------------------------------

export function useGetBudgetVariance(periodId: string, accountCode?: string) {
	return useQuery({
		queryKey: ["accounting", "budget-variance", periodId, accountCode],
		queryFn: () => client.getBudgetVariance({ periodId, accountCode }),
		enabled: !!periodId,
	});
}

export function useSetBudgetAmount() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<SetBudgetAmountRequest>) =>
			client.setBudgetAmount(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "budget-variance"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Fixed Assets
// ---------------------------------------------------------------------------

export function useListFixedAssets(
	filters?: PartialMessage<ListFixedAssetsRequest>,
) {
	return useQuery({
		queryKey: ["accounting", "fixed-assets", filters],
		queryFn: () =>
			client.listFixedAssets({
				page: filters?.page ?? {
					pageSize: DEFAULT_LIST_PAGE_SIZE,
					pageToken: "",
				},
				...filters,
			}),
	});
}

export function useCreateFixedAsset() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<CreateFixedAssetRequest>) =>
			client.createFixedAsset(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "fixed-assets"] });
		},
	});
}

export function useDisposeFixedAsset() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<DisposeFixedAssetRequest>) =>
			client.disposeFixedAsset(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "fixed-assets"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Credit Check
// ---------------------------------------------------------------------------

export function useCheckCredit() {
	return useMutation({
		mutationFn: (req: {
			partyId: string;
			orderAmount: string;
			currency: string;
		}) => client.checkCredit(req),
	});
}

// ---------------------------------------------------------------------------
// IC & Consolidation
// ---------------------------------------------------------------------------

export function useListIcTransactions(
	filters?: PartialMessage<ListIcTransactionsRequest>,
) {
	return useQuery({
		queryKey: ["accounting", "ic-transactions", filters],
		queryFn: () =>
			client.listIcTransactions({
				page: filters?.page ?? {
					pageSize: DEFAULT_LIST_PAGE_SIZE,
					pageToken: "",
				},
				...filters,
			}),
	});
}

export function useRunConsolidation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<RunConsolidationRequest>) =>
			client.runConsolidation(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "ic-transactions"] });
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Composite hooks
// ---------------------------------------------------------------------------

export function useAccountsByPrefix(prefix: string) {
	const { data: periods } = useListPeriods();
	const currentPeriod = periods?.periods.find(
		(p) => p.status === PeriodStatus.OPEN,
	);
	const periodId = currentPeriod?.periodId ?? "";

	const { data: trialBalance, isLoading } = useGetTrialBalance(
		periodId,
		"accrual",
	);

	const lines = useMemo(
		() =>
			trialBalance?.lines.filter((l) => l.accountCode?.startsWith(prefix)) ??
			[],
		[trialBalance, prefix],
	);

	const totals = useMemo(() => {
		let debits = 0;
		let credits = 0;
		let net = 0;
		for (const line of lines) {
			debits += Number.parseFloat(line.totalDebits || "0");
			credits += Number.parseFloat(line.totalCredits || "0");
			net += Number.parseFloat(line.net || "0");
		}
		return {
			debits: String(debits),
			credits: String(credits),
			net: String(net),
		};
	}, [lines]);

	return { lines, totals, isLoading };
}

// ---------------------------------------------------------------------------
// Account Analysis (Анализ счёта)
// ---------------------------------------------------------------------------

export function useGetAccountAnalysis(
	accountCode: string,
	periodId: string,
	basis?: string,
) {
	return useQuery({
		queryKey: ["accounting", "analysis", accountCode, periodId, basis],
		queryFn: () =>
			client.getAccountAnalysis({
				accountCode,
				periodId,
				basis,
			}),
		enabled: !!accountCode && !!periodId,
	});
}

export function useGetCrossTabBalance(periodId: string, basis?: string) {
	return useQuery({
		queryKey: ["accounting", "crosstab", periodId, basis],
		queryFn: () =>
			client.getCrossTabBalance({
				periodId,
				basis,
			}),
		enabled: !!periodId,
	});
}

// ---------------------------------------------------------------------------
// Batch Operations
// ---------------------------------------------------------------------------

export function useBatchPostEntries() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<BatchPostEntriesRequest>) =>
			client.batchPostEntries(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
		},
	});
}

export function useBatchReverseEntries() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<BatchReverseEntriesRequest>) =>
			client.batchReverseEntries(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Quality Check (Экспресс-проверка)
// ---------------------------------------------------------------------------

export function useRunQualityCheck() {
	return useMutation({
		mutationFn: (req: PartialMessage<RunQualityCheckRequest>) =>
			client.runQualityCheck(req),
	});
}

// ---------------------------------------------------------------------------
// Counterparty Reconciliation (Акт сверки)
// ---------------------------------------------------------------------------

export function useGetCounterpartyRecon(
	counterpartyId: string,
	periodId: string,
	accountCode?: string,
) {
	return useQuery({
		queryKey: [
			"accounting",
			"counterparty-recon",
			counterpartyId,
			periodId,
			accountCode,
		],
		queryFn: () =>
			client.getCounterpartyRecon({
				counterpartyId,
				periodId,
				accountCode,
			} as PartialMessage<GetCounterpartyReconRequest>),
		enabled: !!counterpartyId && !!periodId,
	});
}

// ---------------------------------------------------------------------------
// Regulatory Operations (Регламентные операции)
// ---------------------------------------------------------------------------

export function useListWorkflows(periodId: string) {
	return useQuery({
		queryKey: ["accounting", "workflows", periodId],
		queryFn: () => client.listWorkflows({ periodId }),
		enabled: !!periodId,
	});
}

export function useRunWorkflow() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: { workflowName: string; periodId: string; dryRun: boolean }) =>
			client.runWorkflow(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "entries"] });
			qc.invalidateQueries({ queryKey: ["accounting", "workflows"] });
		},
	});
}

// ---------------------------------------------------------------------------
// Recurring Entries (Повторяющиеся проводки)
// ---------------------------------------------------------------------------

export function useListRecurringEntries() {
	return useQuery({
		queryKey: ["accounting", "recurring"],
		queryFn: () => client.listRecurringEntries({}),
	});
}

export function useCreateRecurringEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<CreateRecurringEntryRequest>) =>
			client.createRecurringEntry(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "recurring"] });
		},
	});
}

export function useUpdateRecurringEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<UpdateRecurringEntryRequest>) =>
			client.updateRecurringEntry(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "recurring"] });
		},
	});
}

export function useDeleteRecurringEntry() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (req: PartialMessage<DeleteRecurringEntryRequest>) =>
			client.deleteRecurringEntry(req),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["accounting", "recurring"] });
		},
	});
}
